import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link, useLocation } from 'react-router';
import { toast } from 'react-toastify';
import { login, clearError } from '../../store/slices/authSlice';
import { buildRegisterPath, resolveReturnPath } from '../../utils/authRedirect';
import styled from 'styled-components';

const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: linear-gradient(135deg, #a8e6cf 0%, #88d8a3 100%);
`;

const LoginCard = styled.div`
  background-color: var(--bg-primary);
  padding: 2rem;
  border-radius: 0.5rem;
  box-shadow: var(--shadow-md);
  width: 100%;
  max-width: 400px;
`;

const LoginTitle = styled.h1`
  text-align: center;
  font-size: 1.875rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
`;

const LoginSubtitle = styled.p`
  text-align: center;
  font-size: 0.9375rem;
  color: var(--text-secondary);
  margin: 0 0 1.5rem;
  line-height: 1.45;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0.375rem;
  font-size: 0.875rem;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgb(16 185 129 / 0.1);
  }
`;

const PasswordInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const PasswordInput = styled(Input)`
  width: 100%;
  box-sizing: border-box;
`;

const ShowPasswordCheckbox = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: var(--text-secondary);
  cursor: pointer;
  
  input[type="checkbox"] {
    cursor: pointer;
    width: 1rem;
    height: 1rem;
  }
  
  label {
    cursor: pointer;
    user-select: none;
  }
`;

const SubmitButton = styled.button`
  background-color: transparent;
  color: var(--primary-color);
  padding: 0.75rem;
  border: 2px solid var(--primary-color);
  border-radius: 0.375rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;
  margin-top: 1rem;
  
  &:hover:not(:disabled) {
    background-color: var(--primary-color);
    color: white;
  }
  
  &:disabled {
    border-color: var(--text-light);
    color: var(--text-light);
    cursor: not-allowed;
  }
`;

const ForgotPasswordLink = styled.div`
  text-align: right;
  margin-top: 0.5rem;
  
  a {
    color: var(--primary-color);
    text-decoration: none;
    font-size: 0.875rem;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const SignUpSection = styled.div`
  margin-top: 1.75rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--border-color);
  text-align: center;
`;

const SignUpPrompt = styled.p`
  margin: 0 0 0.75rem;
  font-size: 0.9375rem;
  color: var(--text-secondary);
`;

const CreateAccountButton = styled(Link)`
  display: inline-block;
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid var(--primary-color);
  border-radius: 0.375rem;
  background: transparent;
  color: var(--primary-color);
  font-size: 0.9375rem;
  font-weight: 600;
  text-align: center;
  text-decoration: none;
  transition: background-color 0.2s ease, color 0.2s ease;
  box-sizing: border-box;

  &:hover {
    background-color: var(--primary-color);
    color: white;
  }
`;

const LoginPage = () => {
  const [formData, setFormData] = useState({
    email_or_username: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, error, isAuthenticated, user } = useSelector((state) => state.auth);
  const returnTo = resolveReturnPath({ search: location.search, state: location.state });

  useEffect(() => {
    // Only redirect if authenticated AND we have user data (not just isAuthenticated flag)
    // This prevents redirect loop if getUserProfile fails after login
    if (isAuthenticated && user && !loading) {
      const timer = setTimeout(() => {
        navigate(returnTo, { replace: true });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, user, loading, navigate, returnTo]);

  useEffect(() => {
    if (error) {
      // Handle error object - extract message properly
      let errorMessage = 'Login failed';
      
      if (typeof error === 'string') {
        errorMessage = error;
      } else if (error?.error) {
        errorMessage = error.error;
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (typeof error === 'object') {
        // Handle field-specific errors (e.g., {email: ["error"], password: ["error"]})
        const fieldErrors = Object.entries(error)
          .map(([field, messages]) => {
            if (Array.isArray(messages)) {
              return `${field}: ${messages.join(', ')}`;
            }
            return `${field}: ${messages}`;
          })
          .join('\n');
        
        if (fieldErrors) {
          errorMessage = fieldErrors;
        } else {
          errorMessage = JSON.stringify(error);
        }
      }
      
      toast.error(errorMessage);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    dispatch(login({ email_or_username: formData.email_or_username.trim(), password: formData.password }));
  };

  return (
    <LoginContainer>
      <LoginCard>
        <LoginTitle>Sign In</LoginTitle>
        <LoginSubtitle>Welcome back — sign in to your Flea account</LoginSubtitle>
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="email_or_username">Email or Username</Label>
            <Input
              type="text"
              id="email_or_username"
              name="email_or_username"
              value={formData.email_or_username}
              onChange={handleChange}
              required
              placeholder="Enter your email or username"
              autoComplete="username"
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="password">Password</Label>
            <PasswordInputWrapper>
              <PasswordInput
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                placeholder="Enter your password"
              />
              <ShowPasswordCheckbox>
                <input
                  type="checkbox"
                  id="showPassword"
                  checked={showPassword}
                  onChange={(e) => setShowPassword(e.target.checked)}
                />
                <label htmlFor="showPassword">Show password</label>
              </ShowPasswordCheckbox>
            </PasswordInputWrapper>
            <ForgotPasswordLink>
              <Link
                to={
                  formData.email_or_username.trim()
                    ? `/reset-password?identifier=${encodeURIComponent(formData.email_or_username.trim())}`
                    : '/reset-password'
                }
              >
                Forgot Password?
              </Link>
            </ForgotPasswordLink>
          </FormGroup>
          
          <SubmitButton type="submit" disabled={loading}>
            {loading ? 'Signing In...' : 'Sign In'}
          </SubmitButton>
        </Form>

        <SignUpSection>
          <SignUpPrompt>Don&apos;t have an account?</SignUpPrompt>
          <CreateAccountButton to={buildRegisterPath(returnTo)}>
            Create Account
          </CreateAccountButton>
        </SignUpSection>
      </LoginCard>
    </LoginContainer>
  );
};

export default LoginPage;
